/**
 * @file Application providers (1.13.6, F1.3, F2.4).
 *
 * Runtime configuration, the typed API client and the query client. Shorter
 * than the storefront's by design: there is no cart, no consent banner and no
 * toast stack here, because this page has one flow and nothing to notify
 * about that is not already on screen.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiClientProvider } from '../api/ApiClientProvider';
import { createApiClient } from '../api/client';
import { ConfigProvider } from '../lib/config/ConfigProvider';

import type { RuntimeConfig } from '../lib/config/schema';
import type { ReactElement, ReactNode } from 'react';

/** Props for AppProviders. */
export interface AppProvidersProps {
  config: RuntimeConfig;
  children: ReactNode;
}

/**
 * Query defaults.
 *
 * `staleTime` is zero rather than the storefront's thirty seconds: the two
 * things this page asks for are whether there is a session and whether a
 * membership exists, and both are expected to change underneath it while it is
 * open. A cached "not signed in" from thirty seconds ago is the wrong answer
 * on the one page where signing in is the point.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        retry: 1,
        refetchOnWindowFocus: true,
      },
    },
  });
}

/** Wraps the tree with configuration, the API client and the query client. */
export function AppProviders({ config, children }: AppProvidersProps): ReactElement {
  // Created once per mount; the config never changes for the life of the page.
  const [queryClient] = useState(createQueryClient);
  const [apiClient] = useState(() => createApiClient(config.apiBaseUrl));
  return (
    <ConfigProvider config={config}>
      <ApiClientProvider client={apiClient}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </ApiClientProvider>
    </ConfigProvider>
  );
}
